// src/components/layout/Navbar.js
import React, { useState, useEffect, useRef, startTransition } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Search,
  Scale,
  FileBadge,
  Briefcase,
  Settings2,
  LayoutTemplate,
  Plug,
  HelpCircle,
  LogOut,
  ChevronDown,
  Menu,
  X,
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import logo from '../../assets/images/zone-icon.png';
import styles from './Navbar.module.css';
import ChatWidget from '../chat/ChatWidget';

// ─────────────────────────────────────────────────────────────
// DONNÉES DE NAVIGATION
// ─────────────────────────────────────────────────────────────
const getModulesItems = (hasAccess) =>
  [
    hasAccess('fiche_poste') && {
      to:    '/fiche-poste',
      Icon:  FileText,
      label: 'Fiche de Poste',
      desc:  'Notes → fiche professionnelle',
    },
    hasAccess('audit_cv') && {
      to:    '/analyse',
      Icon:  Search,
      label: 'Audit CV',
      desc:  'Analyse de cohérence',
    },
    hasAccess('matching') && {
      to:    '/matching',
      Icon:  Scale,
      label: 'Matching CV / AO',
      desc:  'Score de pertinence',
    },
    hasAccess('generator') && {
      to:    '/process',
      Icon:  FileBadge,
      label: 'Générateur',
      desc:  'Dossier de compétences',
    },
    hasAccess('job_search') && {
      to:    '/job-search',
      Icon:  Briefcase,
      label: "Recherche d'Offres",
      desc:  'Trouver des missions',
    },
  ].filter(Boolean);

const SETTINGS_ITEMS = [
  {
    to:    '/settings/templates',
    Icon:  LayoutTemplate,
    label: 'Modèles de documents',
    desc:  'Gérer vos templates',
  },
  {
    to:    '/settings/boondmanager',
    Icon:  Plug,
    label: 'Connecteurs',
    desc:  'Intégrations tierces',
  },
];

const MODULES_PATHS  = ['/fiche-poste', '/analyse', '/matching', '/process', '/job-search'];
const SETTINGS_PATHS = ['/settings'];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const location         = useLocation();

  const [isMobileOpen,   setIsMobileOpen]   = useState(false);
  const [isScrolled,     setIsScrolled]     = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'modules' | 'settings' | null

  const navRef = useRef(null);

  // ── Permissions ────────────────────────────────────────────
  const hasAccess = (key) => {
    if (!user) return false;
    if (user.permissions && typeof user.permissions[key] !== 'undefined') {
      return user.permissions[key];
    }
    return true;
  };

  const modulesItems = getModulesItems(hasAccess);
  const hasModules   = modulesItems.length > 0;

  // ── Scroll ────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Fermer à chaque changement de route ───────────────────
  useEffect(() => {
    setIsMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // ── Clic extérieur ────────────────────────────────────────
  useEffect(() => {
    const handleOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsMobileOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // ── Scroll body ──────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  // ── Helpers ───────────────────────────────────────────────
  const isParentActive = (paths) =>
    paths.some((p) => location.pathname.startsWith(p));

  const navLinkClass = ({ isActive }) =>
    `${styles.navLink} ${isActive ? styles.active : ''}`;

  const handleLogout = () => {
    startTransition(() => logout());
    navigate('/');
  };

  const closeMobile = () => {
    setIsMobileOpen(false);
    setActiveDropdown(null);
  };

  const toggleMobileDropdown = (name) =>
    setActiveDropdown((prev) => (prev === name ? null : name));

  // Handlers desktop dropdown (souris)
  const openDropdown  = (name) => setActiveDropdown(name);
  const closeDropdown = ()     => setActiveDropdown(null);

  const userInitial = (user?.email?.[0] ?? '?').toUpperCase();

  // ─────────────────────────────────────────────────────────
  return (
    <div ref={navRef}>

      {/* Overlay mobile */}
      {isMobileOpen && (
        <div
          className={styles.overlay}
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* ══ HEADER ══ */}
      <header
        className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}
        role="banner"
      >
        {/* Logo */}
        <NavLink to="/home" className={styles.logoLink} aria-label="Accueil">
          <img src={logo} alt="Zone" className={styles.logo} />
        </NavLink>

        {/* Navigation desktop */}
        <nav className={styles.desktopNav} aria-label="Navigation principale">

          {/* Dropdown : Nos Modules */}
          {hasModules && (
            <DesktopDropdown
              label="Nos Modules"
              isOpen={activeDropdown === 'modules'}
              isActive={isParentActive(MODULES_PATHS)}
              onMouseEnter={() => openDropdown('modules')}
              onMouseLeave={closeDropdown}
              items={modulesItems}
            />
          )}

          {/* Pilotage */}
          <NavLink to="/management" className={navLinkClass}>
            <LayoutDashboard size={15} strokeWidth={2} />
            Pilotage
          </NavLink>

          {/* Dropdown : Configuration */}
          <DesktopDropdown
            label="Configuration"
            isOpen={activeDropdown === 'settings'}
            isActive={isParentActive(SETTINGS_PATHS)}
            onMouseEnter={() => openDropdown('settings')}
            onMouseLeave={closeDropdown}
            items={SETTINGS_ITEMS}
          />

          {/* Aide */}
          <NavLink to="/support" className={navLinkClass}>
            <HelpCircle size={15} strokeWidth={2} />
            Aide
          </NavLink>
        </nav>

        {/* Actions desktop */}
        <div className={styles.actions}>
          <ChatWidget />
          {user && (
            <div className={styles.userBadge} title={user.email}>
              <span className={styles.userAvatar}>{userInitial}</span>
              <span className={styles.userEmail}>{user.email}</span>
            </div>
          )}

          <button
            className={`${styles.btn} ${styles.btnLogout}`}
            onClick={handleLogout}
          >
            <LogOut size={15} strokeWidth={2} />
            Déconnexion
          </button>

          {/* Hamburger */}
          <button
            className={`${styles.hamburger} ${isMobileOpen ? styles.hamburgerOpen : ''}`}
            onClick={() => setIsMobileOpen((p) => !p)}
            aria-label={isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen
              ? <X    size={20} strokeWidth={2} />
              : <Menu size={20} strokeWidth={2} />
            }
          </button>
        </div>
      </header>

      {/* ══ MENU MOBILE ══ */}
      <div
        className={`${styles.mobileNav} ${isMobileOpen ? styles.mobileNavOpen : ''}`}
        aria-hidden={!isMobileOpen}
      >
        <div className={styles.mobileInner}>

          {hasModules && (
            <MobileDropdown
              label="Nos Modules"
              isOpen={activeDropdown === 'modules'}
              isActive={isParentActive(MODULES_PATHS)}
              onToggle={() => toggleMobileDropdown('modules')}
              items={modulesItems}
              onItemClick={closeMobile}
            />
          )}

          <NavLink to="/management" className={navLinkClass} onClick={closeMobile}>
            <LayoutDashboard size={16} strokeWidth={2} />
            Pilotage
          </NavLink>

          <MobileDropdown
            label="Configuration"
            isOpen={activeDropdown === 'settings'}
            isActive={isParentActive(SETTINGS_PATHS)}
            onToggle={() => toggleMobileDropdown('settings')}
            items={SETTINGS_ITEMS}
            onItemClick={closeMobile}
          />

          <NavLink to="/support" className={navLinkClass} onClick={closeMobile}>
            <HelpCircle size={16} strokeWidth={2} />
            Aide
          </NavLink>

          <div className={styles.divider} />

          {user && (
            <div className={styles.mobileUserCard}>
              <span className={styles.mobileAvatar}>{userInitial}</span>
              <div className={styles.mobileUserInfo}>
                <span className={styles.mobileUserLabel}>Connecté en tant que</span>
                <span className={styles.mobileUserEmail}>{user.email}</span>
              </div>
            </div>
          )}

          <button
            className={`${styles.btn} ${styles.btnLogoutMobile}`}
            onClick={() => { closeMobile(); handleLogout(); }}
          >
            <LogOut size={16} strokeWidth={2} />
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DROPDOWN DESKTOP
// Géré par onMouseEnter/Leave → fiable, pas de gap mort
// ─────────────────────────────────────────────────────────────
const DesktopDropdown = ({
  label, isOpen, isActive, onMouseEnter, onMouseLeave, items,
}) => (
  <div
    className={styles.dropdownWrapper}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {/* Trigger */}
    <button
      className={`
        ${styles.navLink}
        ${styles.dropdownTrigger}
        ${isActive || isOpen ? styles.active : ''}
      `}
      type="button"
      aria-expanded={isOpen}
      aria-haspopup="true"
    >
      {label}
      <ChevronDown
        size={13}
        strokeWidth={2.5}
        className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
      />
    </button>

    {/* Panel — visible via classe JS, pas via :hover CSS */}
    <div
      className={`${styles.dropdownPanel} ${isOpen ? styles.dropdownPanelOpen : ''}`}
      role="menu"
    >
      {items.map(({ to, Icon, label: itemLabel, desc }) => (
        <NavLink
          key={to}
          to={to}
          role="menuitem"
          className={({ isActive: ia }) =>
            `${styles.dropdownItem} ${ia ? styles.dropdownItemActive : ''}`
          }
        >
          <span className={styles.itemIconWrap}>
            <Icon size={16} strokeWidth={1.75} />
          </span>
          <span className={styles.itemText}>
            <strong>{itemLabel}</strong>
            {desc && <small>{desc}</small>}
          </span>
        </NavLink>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// DROPDOWN MOBILE — Accordéon animé
// ─────────────────────────────────────────────────────────────
const MobileDropdown = ({
  label, isOpen, isActive, onToggle, items, onItemClick,
}) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    setHeight(isOpen ? contentRef.current.scrollHeight : 0);
  }, [isOpen]);

  return (
    <div className={styles.mobileDropdown}>
      <button
        className={`
          ${styles.mobileDropdownTrigger}
          ${isActive ? styles.triggerActive : ''}
          ${isOpen   ? styles.triggerOpen   : ''}
        `}
        onClick={onToggle}
        aria-expanded={isOpen}
        type="button"
      >
        <span>{label}</span>
        <ChevronDown
          size={15}
          strokeWidth={2.5}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </button>

      {/* Hauteur animée via style inline */}
      <div
        className={styles.accordionWrap}
        style={{ maxHeight: `${height}px` }}
      >
        <div ref={contentRef} className={styles.accordionInner}>
          {items.map(({ to, Icon, label: itemLabel, desc }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onItemClick}
              className={({ isActive: ia }) =>
                `${styles.mobileItem} ${ia ? styles.mobileItemActive : ''}`
              }
            >
              <span className={styles.itemIconWrap}>
                <Icon size={15} strokeWidth={1.75} />
              </span>
              <span className={styles.itemText}>
                <strong>{itemLabel}</strong>
                {desc && <small>{desc}</small>}
              </span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Navbar;